import React, { useEffect, useState } from 'react';
import { StaticPageTitle } from '@common/seo/static-page-title';
import { Trans } from '@ui/i18n/trans';
import { ContentGrid } from '@app/web-player/playable-item/content-grid';
import ShopItem from '@app/web-player/shop/shop-item';
import { PageErrorMessage } from '@common/errors/page-error-message';
import { MediaPageNoResultsMessage } from '@app/web-player/layout/media-page-no-results-message';
import { useAuth } from '@common/auth/use-auth';
import { message } from '@ui/i18n/message';  // Importa a função message
import { toast } from '@ui/toast/toast';      // Importa a função toast
import { useNavigate } from 'react-router-dom'; // Importa o hook useNavigate

interface ShopItemType {
  id: number;
  title: string;
  description: string;
  price: number;
  image_url: string;
}

export function LojaPage() {
  const { user } = useAuth(); // Obtém informações do usuário logado
  const [shopItems, setShopItems] = useState<ShopItemType[]>([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [isError, setIsError] = useState(false);
  const navigate = useNavigate(); // Inicializa o hook useNavigate

  const fetchShopItems = async () => {
    try {
      const itemsResponse = await fetch('/api/shop_items.php');
      const textData = await itemsResponse.text();
      console.log(textData);
      
      const itemsData = JSON.parse(textData);
      
      if (!itemsResponse.ok) {
        throw new Error('Erro ao carregar itens da loja');
      }
      
      setShopItems(itemsData);
    } catch (error) {
      console.error(error);
      setIsError(true);
    }
  };

  const fetchWalletBalance = async () => {
    if (!user) return; // Verifica se o usuário está logado

    try {
      const balanceResponse = await fetch(`/api/wallet_balance.php?userId=${user.id}`); // Passa o ID do usuário na URL
      const balanceData = await balanceResponse.json();

      if (!balanceResponse.ok) {
        throw new Error('Erro ao carregar saldo da carteira');
      }

      setWalletBalance(balanceData.wallet_balance);
    } catch (error) {
      console.error(error);
      setIsError(true);
    }
  };

  const handlePurchase = async (item: ShopItemType) => {
    if (walletBalance < item.price) {
      toast.danger(message('Saldo insuficiente para realizar a compra.'));
      return;
    }
  
    if (!user) return; // Verifica se o usuário está logado
  
    try {
      const purchaseResponse = await fetch('/api/purchase.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ itemId: item.id, userId: user.id }), // Inclui o ID do usuário no corpo da requisição
      });
  
      // Log do status HTTP da resposta
      console.log('Status da resposta HTTP:', purchaseResponse.status);
  
      const purchaseResult = await purchaseResponse.json();
  
      // Log detalhado da resposta para depuração
      console.log('Resposta da compra:', purchaseResult);
  
      // Caso a resposta não seja OK (status diferente de 200)
      if (!purchaseResponse.ok) {
        console.error('Erro HTTP ao realizar a compra:', purchaseResponse.statusText);
        toast.danger(message(`Erro ao realizar a compra. Status: ${purchaseResponse.status}`));
        return;
      }
  
      if (purchaseResult.error) {
        console.error('Erro retornado pela API:', purchaseResult.error);
        toast.danger(message(purchaseResult.error || 'Erro ao realizar a compra.'));
        return;
      }
  
      // Atualiza o saldo do usuário
      setWalletBalance(prevBalance => prevBalance - item.price);
      toast.positive(message('Compra realizada com sucesso!'));

      // Redireciona para 'historico-compras' se a compra for realizada com sucesso
      navigate('/historico-compras');
    } catch (error) {
      console.error('Erro na requisição de compra:', error);
      toast.danger(message('Erro ao realizar a compra. Por favor, tente novamente mais tarde.'));
    }
  };
  
  
  

  useEffect(() => {
    fetchShopItems();
    fetchWalletBalance();
  }, [user]);

  if (isError) {
    return <PageErrorMessage />;
  }

  return (
    <div>
      <StaticPageTitle>
        <Trans message="Loja" />
      </StaticPageTitle>
      <h1 className="text-2xl font-semibold mb-4">
        <Trans message="Clube de Vantagens" />
      </h1>

      <div className="mt-4">
        <ContentGrid>
          {shopItems.map(item => (
            <ShopItem
              key={item.id}
              id={item.id}
              title={item.title}
              description={item.description}
              price={item.price}
              imageUrl={item.image_url}
              onPurchase={() => handlePurchase(item)}
            />
          ))}
        </ContentGrid>
      </div>

      {!shopItems.length && (
        <MediaPageNoResultsMessage
          className="mt-34"
          description={<Trans message="Não há itens disponíveis na loja." />}
        />
      )}
    </div>
  );
}
